import React, { useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { useDispatch, useSelector } from "react-redux";
import { getBlogById, getBlogs } from "../../redux/blogSlice";
import { calculateReadTime } from "../../utils/blogUtils";
import { FiLoader, FiCalendar, FiClock, FiUser } from "react-icons/fi";
import fallbackImage from "../../assets/images/blogs/blog.svg";

const formatDate = (dateStr) => {
  if (!dateStr) return null;
  try {
    return new Date(dateStr).toLocaleDateString("en-GB", {
      day: "2-digit", month: "long", year: "numeric",
    });
  } catch {
    return null;
  }
};

const BlogDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const { currentBlog: blog, blogs, loading } = useSelector((state) => state.blogs);

  useEffect(() => {
    dispatch(getBlogById(id));
    if (blogs.length === 0) {
      dispatch(getBlogs({ limit: 6, status: "published" }));
    }
  }, [dispatch, id, blogs.length]);

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen gap-3 text-gray-400">
        <FiLoader className="w-6 h-6 animate-spin" />
        <span>Loading article...</span>
      </div>
    );
  }

  if (!blog) {
    return (
      <div className="container mx-auto px-4 py-8">
        <div className="text-center">
          <h1 className="text-2xl font-bold mb-4">{t("noArticlesFound")}</h1>
          <button
            onClick={() => navigate("/blog")}
            className="bg-[#8f64ea] text-white px-4 py-2 rounded hover:bg-[#342c45]"
          >
            {t("goToBack")}
          </button>
        </div>
      </div>
    );
  }

  const content = blog.content || blog.description || "";
  const readTime = calculateReadTime(content);
  const publishDate = formatDate(blog.createdAt);
  const relatedBlogs = blogs.filter((b) => b._id !== blog._id).slice(0, 6);

  return (
    <div className="container mx-auto px-4 pt-28 pb-8 max-w-4xl">
      {/* Back button */}
      <button
        onClick={() => navigate("/blog")}
        className="inline-flex items-center text-[#8f64ea] hover:text-[#342c45] mb-6"
      >
        <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
        </svg>
        {t("backToBlogs")}
      </button>

      {/* Cover Image */}
      {(blog.image || blog.coverImage) && (
        <div className="w-full h-72 md:h-96 rounded-2xl overflow-hidden mb-8">
          <img
            src={blog.image || blog.coverImage}
            alt={blog.title}
            className="w-full h-full object-cover"
            onError={(e) => { e.target.src = fallbackImage; }}
          />
        </div>
      )}

      {/* Title */}
      <h1 className="font-roboto text-3xl md:text-4xl text-text mb-4 leading-tight">
        {blog.title}
      </h1>

      {/* Meta */}
      <div className="flex flex-wrap items-center gap-4 mb-8 text-gray-500 font-poppins text-sm">
        <span className="flex items-center gap-1.5">
          <FiClock className="w-4 h-4" />
          {readTime} {t("minutes")} {t("readTime")}
        </span>
        {publishDate && (
          <span className="flex items-center gap-1.5">
            <FiCalendar className="w-4 h-4" />
            {publishDate}
          </span>
        )}
        {blog.author && (
          <span className="flex items-center gap-1.5">
            <FiUser className="w-4 h-4" />
            {blog.author}
          </span>
        )}
      </div>

      {/* Content */}
      <div className="prose prose-lg max-w-none">
        <div
          className="font-poppins text-text leading-relaxed space-y-6"
          dangerouslySetInnerHTML={{ __html: content }}
        />
      </div>

      {/* Related Articles */}
      {relatedBlogs.length > 0 && (
        <div className="mt-12 pt-8 border-t border-gray-200">
          <h3 className="font-roboto text-2xl mb-6 text-center">{t("relatedArticles")}</h3>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {relatedBlogs.map((other) => (
              <div
                key={other._id}
                onClick={() => navigate(`/blog/${other._id}`)}
                className="block p-6 border border-gray-200 rounded-lg hover:shadow-lg transition-all cursor-pointer hover:border-blue-300"
              >
                {other.image && (
                  <img
                    src={other.image}
                    alt={other.title}
                    className="w-full h-32 object-cover rounded-lg mb-3"
                    onError={(e) => { e.target.style.display = "none"; }}
                  />
                )}
                <h4 className="font-poppins text-lg mb-2 text-[#8f64ea] hover:text-[#342c45] line-clamp-2">
                  {other.title}
                </h4>
                <p className="text-gray-600 text-sm line-clamp-3">
                  {other.description?.replace(/<[^>]*>/g, "").slice(0, 120)}...
                </p>
                <span className="text-[#8f64ea] text-sm mt-2 inline-block">
                  {t("readMore")} →
                </span>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

export default BlogDetail;
